<template>
  <div>
    <div v-if="$apollo.loading || !organization">
      <spinner />
    </div>
    <v-layout v-else class="main-page">
      <div class="sidebar-wrapper">
        <div v-if="!isDesktop" class="sidebar-nav-container">
          <div v-if="!sidebar" class="sidebar-nav">
            <div class="sidebar-nav-title">{{ navigationLabel(navbarName) }}</div>
            <div class="sidebar-nav-img-container">
              <img
                class="sidebar-nav-img"
                src="/assets/sidebar-icons/menu-icon.svg"
                :alt="$adminT('navigation.menu', 'Menu')"
                @click="showSidebar()"
              >
            </div>
          </div>
        </div>
        <v-layout else class="sidebar-container">
          <div v-if="sidebar" class="sidebar-holder">
            <div class="sidebar-header">
              <div class="sidebar-header-back">
                <span class="go-back-container" @click="goBack()">
                  <v-icon small class="back-icon"> arrow_back </v-icon>
                  {{ $adminT('navigation.back_to_home', 'Back to home') }}
                </span>
                <v-spacer v-if="!isDesktop" />
                <v-icon v-if="!isDesktop && sidebar" color="#333333" @click="showSidebar()">
                  close
                </v-icon>
              </div>
              <div class="sidebar-info">
                <div class="organization-info">
                  <div
                    :style="{
                      backgroundImage: 'url(' + organization.logo + ')',
                      backgroundColor: organization.logo ? 'transparent' : '#ffffff25',
                    }"
                    justify-center
                    align-end
                    class="image-container"
                  />
                  <h3 class="organization-title">
                    {{ organization.publicName }}
                  </h3>
                </div>
                <div class="organization-active-events">
                  {{
                    organization.liveEventsCount !== 0
                      ? `${organization.liveEventsCount} ${$adminT('navigation.live_events_count_suffix', 'live events')}`
                      : $adminT('navigation.no_live_events', 'No live events')
                  }}
                </div>
              </div>
            </div>
            <sidebar :sidebar-config="sidebarConfig" :request-count="requestCount" />
          </div>
        </v-layout>
      </div>
      <v-flex
        v-show="isDesktop || (!isDesktop && !sidebar)"
        :class="['screens', {
          'screens--without-overflow-x': shouldRemoveScreenOverflowX,
          'screens--subscription-plans': $route.name === routes.theaterOrganization.theaterOrganizationTheaterSubscriptionsPlan,
        }]"
      >
        <router-view :user="me" />
      </v-flex>
    </v-layout>
  </div>
</template>

<script>
import { navigationLabel } from '../navigation-labels'
import Sidebar from '../Sidebar.vue'
import { theaterOrganizationSidebarConfiguration } from './theater-organization-sidebar-configuration'
import moment from 'moment'
import { GET_ORGANIZATION } from '@/graphql/organization.graphql'
import { getAuthorization } from '@/http'

export default {
  name: 'organization-theater-sidebar',
  components: { Sidebar },
  props: {
    organizationSlug: {
      type: String,
    },
  },
  data() {
    return {
      sidebarConfig: [],
      requestCount: this.requestCount,
      eventSlug: this.eventSlug,
      location: this.location,
      eventTitle: this.eventTitle,
      eventCode: this.eventCode,
      dateAndTime: '',
      me: {},
      organizationRole: null,
      sidebar: window.innerWidth > 800,
      isDesktop: window.innerWidth > 800,
      navbarName: this.$router.history.current.meta.title,
      organization: null,
      moment,
    }
  },
  computed: {
    shouldRemoveScreenOverflowX() {
      const routeNamesToRemove = [
        this.routes.theaterOrganization.theaterLiveEvents,
        this.routes.theaterOrganization.theaterPastEvents,
      ]

      return routeNamesToRemove.includes(this.$route.name)
    },
  },
  watch: {
    $route() {
      this.navbarName = this.$router.history.current.meta.title
      this.sidebar = this.isDesktop
    },
  },
  async created() {
    const auth = await getAuthorization(null, this.organizationSlug, null)
    this.organizationRole = auth.organizationRole
    this.sidebarConfig = theaterOrganizationSidebarConfiguration
      .filter(route => this.shouldRender(route))
      .map(route => {
        if (route.subroutes) {
          route.subroutes = route.subroutes.filter(subroute => this.shouldRender(subroute))
        }

        return route
      })
    window.addEventListener('resize', this.resizeHandler)
  },
  destroyed() {
    window.removeEventListener('resize', this.resizeHandler)
  },
  apollo: {
    getOrganization: {
      query: GET_ORGANIZATION,
      variables() {
        return { organizationSlug: this.organizationSlug }
      },
      error() {
        this.goTo(this.routes.organizer.organizations)
      },
      result({ data, loading }) {
        if (!loading && data) {
          this.organization = data.getOrganization
        }
      },
    },
  },
  methods: {
    navigationLabel,
    hasUserRoles(route) {
      return route.userType ? route.userType.includes(this.me.type) : true
    },
    hasOrganizationRole(route) {
      if (!route.allowedOrganizationRoles) return true

      return this.organizationRole
        ? route.allowedOrganizationRoles.includes(this.organizationRole)
        : true
    },
    shouldRender(route) {
      return this.hasUserRoles(route, this.me) && this.hasOrganizationRole(route)
    },
    resizeHandler() {
      if (window.innerWidth > 800) {
        this.sidebar = true
        this.isDesktop = true
      } else {
        // Mobile browser chrome can resize the viewport while the menu is scrolling.
        if (this.isDesktop) {
          this.sidebar = false
        }
        this.isDesktop = false
      }
    },
    showSidebar() {
      this.sidebar = !this.sidebar
    },
    goBack() {
      this.$router.push('/')
    },
  },
}
</script>
<style scoped>
.main-page {
  display: grid;
  grid-template-columns: 22rem auto;
}
.sidebar-container {
  position: sticky;
  top: 5rem;
}
.sidebar-wrapper {
  grid-column: 1/2;
}
.screens {
  grid-column: 2/3;
  padding: 0 2rem;
  overflow-x: scroll;
}
.sidebar-header {
  background: #f5f5f5;
  border-radius: 0px 12px 12px 0px;
  padding: 1.5rem 1.5rem 1.5rem 2rem;
  margin-bottom: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}
.sidebar-header-back {
  display: flex;
  align-items: center;
  font-family: 'Raleway', sans-serif;
  font-weight: 700;
  font-size: 1rem;
  line-height: 120%;
  color: #ff4c00;
}
.back-icon {
  font-size: inherit !important;
  color: #0277bd !important;
  margin-right: 8px;
}
.go-back-container {
  cursor: pointer;
  color: #0277bd;
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 700;
  font-size: 18px;
  text-align: center;
  text-transform: uppercase;
  font-feature-settings: 'pnum' on, 'lnum' on;
  display: flex;
  align-items: center;
}
.sidebar-header-title {
  font-family: 'Raleway', sans-serif;
  font-weight: 600;
  word-break: break-word;
  font-size: 1.5rem;
  line-height: 120%;
  letter-spacing: -0.03em;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #0277bd;
  margin-bottom: 0.5rem;
}
.sidebar-header-subtitle {
  font-family: 'Source Sans Pro';
  font-weight: 400;
  font-size: 0.875rem;
  line-height: 120%;
  color: #666666;
}
.sidebar-view-event {
  font-family: 'Raleway', 'sans-serif';
  font-size: 1rem;
  font-weight: 700;
  line-height: 120%;
  color: #0277bd;
  width: fit-content;
  cursor: pointer;
  margin-top: 0.5rem;
}
.view-icon {
  margin-left: 0.2rem;
  filter: invert(30%) sepia(56%) saturate(2324%) hue-rotate(180deg) brightness(94%) contrast(98%);
}
.scanner-code {
  margin-bottom: 0.25rem;
  font-family: 'Source Sans Pro';
  font-weight: 400;
  font-size: 0.875rem;
  line-height: 120%;
  color: #666666;
}
.sidebar-info {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}
.organization-info {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
}
.image-container {
  height: 1.5rem;
  width: 1.5rem;
  border-radius: 1rem !important;
  background-size: cover;
  border: 2px solid #d9d9d9;
}
.organization-title {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 600;
  font-size: 20px;
  line-height: 120%;
  letter-spacing: -0.03em;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #666666;
}
.organization-active-events {
  font-family: 'Source Sans Pro';
  font-style: normal;
  font-weight: 400;
  font-size: 1rem;
  line-height: 150%;
  color: #666666;
}
@media only screen and (max-width: 800px) {
  .main-page {
    grid-template-columns: 100%;
    padding: 0;
  }
  .sidebar-wrapper {
    grid-column: 1;
    position: relative;
    margin: 0 -24px;
  }
  .sidebar-nav-container {
    background-color: #f5f5f5;
  }
  .sidebar-nav {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.125rem 1.5rem;
    margin-bottom: 2rem;
  }
  .sidebar-nav-title {
    font-family: 'Raleway', sans-serif;
    font-style: normal;
    font-weight: 600;
    font-size: 1.5rem;
    line-height: 120%;
    letter-spacing: -0.03em;
    font-feature-settings: 'pnum' on, 'lnum' on;
    color: #ff4c00;
  }
  .sidebar-container {
    margin: 0;
  }
  .sidebar-holder {
    width: 100%;
  }
  .sidebar-header {
    border-radius: 2rem;
  }
  .screens {
    grid-column: 1;
    width: 100%;
    padding: 0;
  }
}

.screens--without-overflow-x {
  overflow-x: unset;
}

@media only screen and (max-width: 800px) {
  .screens--subscription-plans {
    /* The mobile list uses document scrolling. overflow-x: scroll also makes
       overflow-y: auto, creating a second scroll container on touch devices. */
    overflow: visible;
  }
}
</style>
